import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { getServiceIcon } from "@/lib/serviceIcons";
import InfoModal from "@/components/app/InfoModal";
import ServiceOnboardingModal from "@/components/app/ServiceOnboardingModal";

const badgeCls = {
  managed: "badge-managed", ai: "badge-ai", automation: "badge-auto",
  "self-serve": "badge-self", "pre-made": "badge-pre",
};

export default function Directory() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [infoSvc, setInfoSvc] = useState(null);
  const [onboardSvc, setOnboardSvc] = useState(null);

  const load = () => api.getServices().then(setServices).catch(() => {});
  useEffect(() => { load(); }, []);

  const startPrice = (s) => (s.packages && s.packages.length ? Math.min(...s.packages.map((p) => p.price)) : s.price);

  return (
    <div className="h-full overflow-y-auto p-6 md:p-10 pb-24 relative hide-scrollbar">
      <div className="max-w-7xl mx-auto space-y-8 fade-in relative z-10">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tight font-display">Service Directory</h2>
          <p className="text-sm text-zinc-400 mt-1">Browse and activate premium marketing services and tools.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {services.map((s) => {
            const Icon = getServiceIcon(s.icon);
            return (
              <div key={s.slug} className="glass-panel rounded-2xl overflow-hidden flex flex-col group">
                <div className="p-6 flex-1 cursor-pointer" onClick={() => navigate(`/app/service/${s.slug}`)}>
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 group-hover:text-primary-400 group-hover:bg-primary-900/30 transition-colors shadow-inner"><Icon size={22} /></div>
                    <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${badgeCls[s.type] || "badge-self"}`}>{s.badge}</span>
                  </div>
                  <h3 className="font-bold text-xl text-white mb-2 group-hover:text-primary-300 transition-colors">{s.title}</h3>
                  <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-[0.15em] mb-4">{s.category}</p>
                  <p className="text-sm text-zinc-400 leading-relaxed line-clamp-2">{s.short_desc}</p>
                </div>
                <div className="px-6 py-5 border-t border-white/10 bg-black/20 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider block">Starting at</span>
                      <span className="font-bold text-white text-lg">${startPrice(s).toLocaleString()}</span>
                    </div>
                    <button onClick={() => setInfoSvc(s)} className="text-xs font-bold text-zinc-400 hover:text-white transition-colors flex items-center">More Info <ArrowRight size={13} className="ml-1" /></button>
                  </div>
                  <button onClick={() => setOnboardSvc(s)} className="w-full py-2.5 bg-white text-black hover:bg-zinc-200 rounded-lg text-sm font-bold transition-colors shadow-[0_0_10px_rgba(255,255,255,0.1)]">Add to Profile</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <InfoModal open={!!infoSvc} service={infoSvc} onClose={() => setInfoSvc(null)}
        onViewPage={() => { const sl = infoSvc.slug; setInfoSvc(null); navigate(`/app/service/${sl}`); }}
        onAdd={() => { setOnboardSvc(infoSvc); setInfoSvc(null); }} />
      <ServiceOnboardingModal open={!!onboardSvc} service={onboardSvc} onClose={() => setOnboardSvc(null)} onDone={() => { load(); navigate("/app/workspaces"); }} />
    </div>
  );
}
